import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import MarketId from 'components/shared/MarketId';
import Row from 'components/shared/Row';
import Text from 'components/shared/Text';
import Spinner from 'components/shared/Spinner';
import ButtonText from 'components/shared/ButtonText';
import SVG from 'components/icons/SVG';
import { useTickers } from 'helpers/hooks';
import { formatAmount } from '@nbx/frontend-helpers/format';
import { isNullish } from 'helpers/objects';
import { isNaNish } from 'helpers/balances';
import { comparePropsIgnoringFunctions } from 'helpers/memo';
import { useSortedTickers } from 'helpers/hooks/useSortedTickers';
import styles from './styles.module.scss';
import { MarketAlertsToggle, useShowMarketAlertsToggle } from './MarketAlertsToggle';

const microapps = window.MICROFRONTENDS_CONFIG;

function MarketMobileDropdown(props) {
  const { t } = useTranslation;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [cachedTickers, setCachedTickers] = useState([]);
  const tickers = useTickers({ vetoCondition: !dropdownOpen });
  const wrapperRef = useRef();

  const showMarketAlertsToggle = useShowMarketAlertsToggle({ pair: props.pair });

  const toggleDropdown = React.useCallback(() => {
    const body = document.querySelector('body');
    if (!dropdownOpen) {
      body.classList.add('noscroll');
      setDropdownOpen(true);
      return;
    }
    body.classList.remove('noscroll');
    setDropdownOpen(false);
  }, [dropdownOpen]);

  const closeOnClickOutside = useCallback(
    e => {
      if (dropdownOpen && !wrapperRef.current?.contains(e.target)) {
        toggleDropdown();
      }
    },
    [dropdownOpen, toggleDropdown]
  );

  useEffect(() => {
    window.addEventListener('click', closeOnClickOutside, { passive: true });
    return () => {
      window.removeEventListener('click', closeOnClickOutside);
    };
  }, [dropdownOpen, closeOnClickOutside]);

  useEffect(() => {
    if (tickers.data.length > 0 && !tickers.loading && !tickers.error) setCachedTickers(tickers.data);
  }, [tickers.loading, tickers.error, tickers.data]);

  const isDifferentPair = useCallback(ticker => ticker.data.pair !== props.pair, [props.pair]);
  const sortedTickers = useSortedTickers({
    tickers: cachedTickers,
    filter: isDifferentPair
  });

  const getChangeFigure = ticker => {
    let { isChangeUp } = ticker;
    let changeUpSymbol = isChangeUp ? '+' : '-';
    let changeFloat = Math.abs(parseFloat(ticker?.change));
    const noData = isNullish(ticker.change) || isNaNish(changeFloat) || changeFloat === 0;
    if (noData) {
      changeFloat = 0;
      isChangeUp = null;
      changeUpSymbol = '';
    }
    return (
      <Text
        styles={[
          styles.changeFigure,
          {
            [styles.changeUp]: !noData && isChangeUp,
            [styles.changeDown]: !noData && !isChangeUp
          }
        ]}
        monospaced
        muted={noData}>
        {changeUpSymbol}
        {changeFloat.toFixed(2)}%
      </Text>
    );
  };

  const refetchDataOnError = () => {
    tickers.refetch();
    if (props.preferences.error) props.preferences.refetch();
  };

  const renderDropdownList = () => {
    if (tickers.error) {
      return (
        <div className={styles.errorContainer}>
          <Text>{t('error_title_generic')}</Text>
          <ButtonText underlined onClick={refetchDataOnError}>
            {t('common_try_again')}
          </ButtonText>
        </div>
      );
    }
    if (cachedTickers.length === 0 && tickers.loading) {
      return (
        <div className={styles.spinnerContainer}>
          <Spinner />
        </div>
      );
    }
    return sortedTickers.map(ticker => {
      const link = `${microapps.routes.trading.path.split(':')[0]}${ticker.pair}`;
      return (
        <Row to={link} onClick={toggleDropdown} key={ticker.id} styles={[styles.dropdownRow]}>
          <MarketId pair={ticker.pair} iconSize="18" />
          <span>
            <Text weight="medium" monospaced styles={[styles.priceText]}>
              {!isNaNish(ticker.price) ? formatAmount(ticker.price, ticker.minTickLength) : '-'}
            </Text>
            {getChangeFigure(ticker)}
          </span>
        </Row>
      );
    });
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        className={classnames([styles.mobileMarkets, { [styles.dropdownOpen]: dropdownOpen }])}
        type="button"
        onClick={toggleDropdown}>
        <span className={styles.buttonContents}>
          <div className={styles.marketWrapper}>
            <MarketId pair={props.pair} iconSize="25" isHeading />
            {showMarketAlertsToggle && !tickers.loading && (
              <div className={styles.marketAlertsToggle}>
                <MarketAlertsToggle pair={props.pair} />
              </div>
            )}
          </div>
          <SVG icon="arrowdown" styles={[styles.dropdownArrow, { [styles.arrowOpen]: dropdownOpen }]} />
        </span>
      </button>
      {dropdownOpen && (
        <div className={styles.dropdownContainer}>
          <div className={styles.dropdown}>{renderDropdownList()}</div>
        </div>
      )}
    </div>
  );
}

MarketMobileDropdown.propTypes = {
  pair: PropTypes.string.isRequired,
  preferences: PropTypes.object.isRequired
};

export default memo(MarketMobileDropdown, comparePropsIgnoringFunctions);
